import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, type LocationImportResponse, type LocationImportRow } from '../api';
import { Badge, Button, Card, Checkbox, ErrorMessage, Notice, Textarea, plural } from '../ui';

const TEMPLATE = 'Code,Name,Warehouse,Walk order,Parent,Notes\nA01,Shelf A,main,10,,\nA01-1,Drawer 1,main,11,A01,Small plates\nA01-2,Drawer 2,main,12,A01,\nB01,Shelf B,main,20,,\n';

const TONE: Record<LocationImportRow['status'], 'green' | 'blue' | 'slate' | 'red'> = {
  new: 'green',
  update: 'blue',
  unchanged: 'slate',
  exists: 'slate',
  invalid: 'red',
};
const LABEL: Record<LocationImportRow['status'], string> = {
  new: 'New',
  update: 'Update',
  unchanged: 'No change',
  exists: 'Already there',
  invalid: 'Problem',
};

/** Creates bins from a CSV: choose a file or paste it, look at what would happen, then create them. */
export function LocationCsvImport() {
  const qc = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => api<LocationImportResponse>('/api/v1/locations/import', { body: { content, dryRun, updateExisting } }),
    onSuccess: ({ result }) => {
      if (result.dryRun) return;
      const { created, updated } = result.summary;
      setDone(`Created ${plural(created, 'bin')}${updated ? ` and updated ${updated}` : ''}.`);
      setContent('');
      setFileName(null);
      run.reset();
      void qc.invalidateQueries({ queryKey: ['locations'] });
    },
  });
  const preview = run.data?.result.dryRun ? run.data.result : null;
  const change = (fn: () => void) => {
    fn();
    setDone(null);
    run.reset();
  };

  const template = () => {
    const url = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bins-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card
      title="Import bins from a CSV"
      actions={
        <Button small variant="ghost" onClick={template}>
          Download a template
        </Button>
      }
    >
      <p className="mb-3 text-sm text-slate-600">
        One bin per row. Only <strong>Code</strong> (or Bin) is needed. Name, Warehouse, Walk order, Parent and Notes are optional, in any order. A plain list of codes works too. Bins without a walk order are numbered in the order they appear in the file, so list them the way you walk them.
      </p>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={file}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="text-sm"
            aria-label="Choose a CSV file"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const text = await f.text();
              change(() => {
                setContent(text);
                setFileName(f.name);
              });
            }}
          />
          {fileName && <span className="text-xs text-slate-500">{fileName}</span>}
        </div>
        <Textarea
          rows={4}
          aria-label="Or paste the CSV here"
          placeholder={'Or paste it here, for example:\nCode,Name\nA01,Shelf A\nA02,Shelf B'}
          value={content}
          onChange={(e) => change(() => { setContent(e.target.value); setFileName(null); })}
          className="font-mono text-xs"
        />
        <Checkbox label="Update bins that already exist, using the columns in the file (a blank cell never clears anything)" checked={updateExisting} onChange={(v) => change(() => setUpdateExisting(v))} />
        <div className="flex gap-2">
          <Button variant="secondary" disabled={!content.trim() || run.isPending} onClick={() => run.mutate(true)}>
            {run.isPending && run.variables === true ? 'Reading…' : 'Preview'}
          </Button>
          {preview && preview.summary.created + preview.summary.updated > 0 && (
            <Button disabled={run.isPending} onClick={() => run.mutate(false)}>
              {run.isPending && run.variables === false
                ? 'Creating…'
                : `Create ${plural(preview.summary.created, 'bin')}${preview.summary.updated ? ` and update ${preview.summary.updated}` : ''}`}
            </Button>
          )}
        </div>
        <ErrorMessage error={run.error} />
        {done && <Notice tone="green">{done}</Notice>}

        {preview && (
          <div className="space-y-2">
            <Notice tone={preview.summary.invalid + preview.summary.skipped > 0 ? 'amber' : 'blue'}>
              {plural(preview.summary.created, 'new bin')}
              {preview.summary.updated > 0 && `, ${preview.summary.updated} to update`}
              {preview.summary.existing > 0 && `, ${preview.summary.existing} already there and left alone`}
              {preview.summary.unchanged > 0 && `, ${preview.summary.unchanged} with nothing to change`}
              {preview.summary.invalid + preview.summary.skipped > 0 && `. ${plural(preview.summary.invalid + preview.summary.skipped, 'row')} cannot be used and will be left out`}.
              {preview.warnings.map((w) => (
                <span key={w} className="block text-xs">
                  {w}
                </span>
              ))}
            </Notice>
            <div className="max-h-80 overflow-auto rounded-lg border border-slate-200">
              <table className="data w-full min-w-[40rem] text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr>
                    <th>Line</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Warehouse</th>
                    <th className="text-right">Walk order</th>
                    <th>Parent</th>
                    <th>What happens</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.skipped.map((s) => (
                    <tr key={`s${s.line}`}>
                      <td>{s.line}</td>
                      <td colSpan={5} className="text-slate-400">
                        (cannot be read)
                      </td>
                      <td>
                        <Badge tone="red">Left out</Badge> <span className="text-xs text-slate-600">{s.reason}</span>
                      </td>
                    </tr>
                  ))}
                  {preview.rows.slice(0, 300).map((r) => (
                    <tr key={`${r.line}-${r.code}`}>
                      <td>{r.line}</td>
                      <td className="font-medium">{r.code}</td>
                      <td>{r.name ?? <span className="text-slate-400">—</span>}</td>
                      <td>{r.warehouse}</td>
                      <td className="text-right tabular-nums">{r.walkOrder ?? <span className="text-slate-400">—</span>}</td>
                      <td>{r.parent ?? <span className="text-slate-400">—</span>}</td>
                      <td>
                        <Badge tone={TONE[r.status]}>{LABEL[r.status]}</Badge> {r.reason && <span className="text-xs text-slate-600">{r.reason}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.rows.length > 300 && <p className="text-xs text-slate-500">Showing the first 300 of {preview.rows.length} rows. All of them are imported.</p>}
          </div>
        )}
      </div>
    </Card>
  );
}
